import { useState, useRef, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import { usePermission } from '../../hooks/usePermission';
import { TopNavigationBar } from '../components/TopNavigationBar';
import { resolveMenuConfig } from '../constants/menuCatalog';
import { useGetCurrentCompanyQuery } from '../../store/api/companyApi';

function MainLayout() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { data: company } = useGetCurrentCompanyQuery();
  const menuScrollRef = useRef(null);

  const scrollMenu = (direction) => {
    if (menuScrollRef.current) {
      const scrollAmount = 200;
      menuScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    if (menuScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = menuScrollRef.current;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
    }
  };



  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Global shortcut F2 or Alt+N to open New Sales Order from any page
      if (e.key === 'F2' || (e.altKey && e.key.toLowerCase() === 'n')) {
        // If already on the new sales order page, let the form's local F2 handler process it
        if (window.location.pathname === '/sales/orders/new' || window.location.pathname.startsWith('/sales/orders/new')) {
          return;
        }
        e.preventDefault();
        handleNavigate('/sales/orders/new');
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isBlocked]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    handleNavigate('/login');
  };

  const handleNavigate = (path) => {
    if (isBlocked) {
      setPendingPath(path);
      setIsNavigatingAway(true);
    } else {
      if (path === '/login') {
        dispatch(logout());
      }
      navigate(path);
      if (mobileMenuOpen) handleMobileMenuClose();
    }
  };

  const confirmNavigation = () => {
    setIsBlocked(false);
    setIsNavigatingAway(false);
    if (pendingPath === '/login') {
      dispatch(logout());
    }
    navigate(pendingPath);
    if (mobileMenuOpen) handleMobileMenuClose();
    setPendingPath(null);
  };

  const cancelNavigation = () => {
    setIsNavigatingAway(false);
    setPendingPath(null);
  };

  const { hasModuleAccess, isAdmin } = usePermission();

  const menuItems = useMemo(() => {
    const configuredItems = resolveMenuConfig(company?.menuOrder);
    return configuredItems.filter((item) => {
      if (user?.isSuperAdmin) {
        return item.isSuper === true;
      }
      if (item.isSuper) return false;
      if (item.visible === false) return false;
      return isAdmin || hasModuleAccess(item.module);
    });
  }, [company?.menuOrder, user?.isSuperAdmin, isAdmin, hasModuleAccess]);

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    if (path === '/products') {
      return (
        (location.pathname === '/products' || location.pathname.startsWith('/products/')) &&
        !location.pathname.startsWith('/products/categories')
      );
    }
    if (path === '/quotations') return location.pathname === '/quotations';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const activeItem = menuItems.find((item) => isActive(item.path));

  useEffect(() => {
    const el = menuScrollRef.current;
    if (el) {
      checkScroll();
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);

      const observer = new ResizeObserver(checkScroll);
      observer.observe(el);

      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        observer.disconnect();
      };
    }
  }, [menuItems]);

  const isPosScreen = location.pathname.startsWith('/sales/orders') && !location.pathname.includes('/print');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to right, #E8F5E9 0%, #F1F8E9 30%, #FFF9E6 70%, #FFFEF5 100%)',
        backgroundAttachment: 'fixed',
        '@media print': {
          background: '#FFFFFF',
          backgroundAttachment: 'scroll',
        },
      }}
    >
      {/* Modern Redesigned Top Navigation Bar */}
      <TopNavigationBar
        onMobileMenuToggle={handleMobileMenuToggle}
        menuItems={menuItems}
        isActive={isActive}
        onNavigate={handleNavigate}
      />

      {/* Modern Horizontal Module Navigation Pills */}
      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          px: { xs: 1, sm: 1.5, md: 2.5 },
          py: 0.75,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          '@media print': { display: 'none' },
        }}
      >
        {canScrollLeft && (
          <IconButton
            size="small"
            onClick={() => scrollMenu('left')}
            sx={{
              width: 28,
              height: 28,
              bgcolor: 'white',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: '#f8fafc' },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}

        <Box
          ref={menuScrollRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flex: 1,
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { height: '3px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(14, 165, 233, 0.3)', borderRadius: '3px' },
            scrollbarWidth: 'none',
          }}
        >
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            return (
              <Chip
                key={item.path}
                icon={<IconComponent sx={{ fontSize: '18px !important', color: active ? 'white !important' : '#64748b !important' }} />}
                label={item.label}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  height: 32,
                  px: 0.5,
                  fontWeight: active ? 700 : 600,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  borderRadius: 2,
                  bgcolor: active ? '#0ea5e9' : '#ffffff',
                  color: active ? '#ffffff' : '#334155',
                  border: active ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                  boxShadow: active ? '0 2px 8px rgba(14, 165, 233, 0.3)' : '0 1px 2px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: active ? '#0284c7' : '#f1f5f9',
                    borderColor: active ? '#0284c7' : '#cbd5e1',
                    transform: 'translateY(-1px)',
                  },
                }}
              />
            );
          })}
        </Box>

        {canScrollRight && (
          <IconButton
            size="small"
            onClick={() => scrollMenu('right')}
            sx={{
              width: 28,
              height: 28,
              bgcolor: 'white',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: '#f8fafc' },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: 280,
            background: 'linear-gradient(to right, #E8F5E9 0%, #F1F8E9 30%, #FFF9E6 70%, #FFFEF5 100%)',
            '@media print': {
              display: 'none',
            },
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)',
              }}
            >
              <LocalFloristIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              YoroInventory
            </Typography>
          </Box>
        </Box>
        <List sx={{ pt: 2 }}>
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleNavigate(item.path);
                  }}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 2,
                    backgroundColor: active ? '#4ECDC4' : 'transparent',
                    color: active ? 'white' : '#2C3E50',
                    '&:hover': {
                      backgroundColor: active ? '#3BA89F' : 'rgba(78, 205, 196, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: active ? 'white' : '#2C3E50',
                      minWidth: 40,
                    }}
                  >
                    <IconComponent />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: active ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: isPosScreen ? { xs: 0.75, sm: 1.25, md: 1.5, lg: 2 } : { xs: 1.25, sm: 2, md: 2.5, lg: 3, xl: 4 },
          py: isPosScreen ? { xs: 0.75, sm: 1 } : { xs: 1.5, sm: 2, md: 2.5 },
          maxWidth: '100%',
          width: '100%',
          minHeight: isPosScreen ? 'calc(100vh - 70px)' : 'calc(100vh - 80px)',
          height: isPosScreen ? { xs: 'auto', lg: 'calc(100vh - 70px)' } : 'auto',
          overflowX: 'hidden',
          overflowY: isPosScreen ? { xs: 'auto', lg: 'hidden' } : 'visible',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          '@media print': {
            px: 0,
            py: 0,
            overflowX: 'visible !important',
            overflow: 'visible !important',
          },
        }}
      >
        <Outlet context={{ setNavigationBlocked: setIsBlocked }} />
      </Box>

      {/* Navigation Block Dialog */}
      <Dialog open={isNavigatingAway} onClose={cancelNavigation}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelNavigation} color="primary">
            Stay
          </Button>
          <Button onClick={confirmNavigation} color="error" autoFocus>
            Leave
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MainLayout;
